import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Tooltip
} from '@mui/material';
import {
  People as PeopleIcon,
  CalendarToday as CalendarTodayIcon,
  MonetizationOn as MonetizationOnIcon,
  MedicalServices as MedicalServicesIcon,
  ArrowForward as ArrowForwardIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';
import AuthContext from '../../context/AuthContext';

// Get API URL from environment variables
import { API_URL } from '../../utils/apiConfig';
import { formatCurrencySync, getOrganizationCurrency, setCurrencyCache, getCurrencySymbol } from '../../utils/currencyUtils';
import { formatDateLocalizedSync, formatDateTimeLocalizedSync } from '../../utils/dateTimeUtils';
import { formatFailedActionMessage } from '../../utils/uiMessages';

// Register ChartJS components
ChartJS.register(ArcElement, ChartTooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalPatients: 0,
    totalAppointments: 0,
    totalRevenue: 0,
    totalTreatments: 0,
    appointmentsToday: [],
    recentPatients: [],
    upcomingAppointments: [],
    revenueByMonth: [],
    appointmentsByType: [],
    retentionAnalytics: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Get token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      // Fetch organization currency and dashboard data in parallel
      const [currency, dashboardResponse] = await Promise.all([
        getOrganizationCurrency(),
        axios.get(`${API_URL}/dashboard`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })
      ]);

      // Set currency cache
      setCurrencyCache(currency);
      
      const data = dashboardResponse.data.data;
      setStats(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(formatFailedActionMessage('load', 'dashboard metrics'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  useEffect(() => {
    const handler = () => fetchDashboardData();
    window.addEventListener('clinic-scope-changed', handler);
    return () => window.removeEventListener('clinic-scope-changed', handler);
  }, [fetchDashboardData]);

  // Format currency using organization settings
  const formatCurrency = (amount) => {
    return formatCurrencySync(amount);
  };

  // Get currency symbol for charts
  const currencySymbol = getCurrencySymbol();

  // Prepare chart data for appointments by type
  const appointmentTypeChartData = {
    labels: stats?.appointmentsByType?.map(item => item.type) || [],
    datasets: [
      {
        label: 'Appointments',
        data: stats?.appointmentsByType?.map(item => item.count) || [],
        backgroundColor: [
          'rgba(54, 162, 235, 0.6)',
          'rgba(75, 192, 192, 0.6)',
          'rgba(255, 206, 86, 0.6)',
          'rgba(153, 102, 255, 0.6)',
          'rgba(255, 99, 132, 0.6)',
          'rgba(255, 159, 64, 0.6)'
        ],
        borderColor: [
          'rgba(54, 162, 235, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(153, 102, 255, 1)',
          'rgba(255, 99, 132, 1)',
          'rgba(255, 159, 64, 1)'
        ],
        borderWidth: 1,
      },
    ],
  };

  // Prepare chart data for revenue by month
  const revenueChartData = {
    labels: stats?.revenueByMonth?.map(item => item.month) || [],
    datasets: [
      {
        label: `Revenue (${currencySymbol})`,
        data: stats?.revenueByMonth?.map(item => item.revenue) || [],
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1,
      },
    ],
  };

  const revenueChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Monthly Revenue',
      },
    },
  };

  const formatTrendLabel = (trendValue) => {
    const trend = Number(trendValue || 0);
    if (trend === 0) return 'No change vs last month';
    return `${trend > 0 ? '+' : ''}${trend}% vs last month`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={fetchDashboardData}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Welcome back, {user?.name || 'User'}! Here's what's happening today.
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4, mt: 1 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" color="text.secondary">
                  Total Patients
                </Typography>
                <Avatar sx={{ bgcolor: 'primary.light' }}>
                  <PeopleIcon />
                </Avatar>
              </Box>
                              <Typography variant="h4">{stats?.totalPatients?.toLocaleString() || '0'}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                {stats?.trends?.patients > 0 ? (
                  <TrendingUpIcon color="success" fontSize="small" />
                ) : (
                  <TrendingDownIcon color="error" fontSize="small" />
                )}
                <Typography 
                  variant="body2" 
                  color={stats?.trends?.patients > 0 ? "success.main" : stats?.trends?.patients < 0 ? "error.main" : "text.secondary"} 
                  sx={{ ml: 0.5 }}
                >
                  {formatTrendLabel(stats?.trends?.patients)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" color="text.secondary">
                  Appointments
                </Typography>
                <Avatar sx={{ bgcolor: 'info.light' }}>
                  <CalendarTodayIcon />
                </Avatar>
              </Box>
                              <Typography variant="h4">{stats?.totalAppointments?.toLocaleString() || '0'}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                {stats?.trends?.appointments > 0 ? (
                  <TrendingUpIcon color="success" fontSize="small" />
                ) : (
                  <TrendingDownIcon color="error" fontSize="small" />
                )}
                <Typography 
                  variant="body2" 
                  color={stats?.trends?.appointments > 0 ? "success.main" : stats?.trends?.appointments < 0 ? "error.main" : "text.secondary"} 
                  sx={{ ml: 0.5 }}
                >
                  {formatTrendLabel(stats?.trends?.appointments)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Revenue Card - Only visible to admin and manager */}
        {(user?.role === 'admin' || user?.role === 'manager') && (
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" color="text.secondary">
                    Revenue
                  </Typography>
                  <Avatar sx={{ bgcolor: 'success.light' }}>
                    <MonetizationOnIcon />
                  </Avatar>
                </Box>
                <Typography variant="h4">{formatCurrency(stats?.totalRevenue || 0)}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  {stats?.trends?.revenue > 0 ? (
                    <TrendingUpIcon color="success" fontSize="small" />
                  ) : (
                    <TrendingDownIcon color="error" fontSize="small" />
                  )}
                  <Typography 
                    variant="body2" 
                    color={stats?.trends?.revenue > 0 ? "success.main" : stats?.trends?.revenue < 0 ? "error.main" : "text.secondary"} 
                    sx={{ ml: 0.5 }}
                  >
                    {formatTrendLabel(stats?.trends?.revenue)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" color="text.secondary">
                  Treatments
                </Typography>
                <Avatar sx={{ bgcolor: 'secondary.light' }}>
                  <MedicalServicesIcon />
                </Avatar>
              </Box>
                              <Typography variant="h4">{stats?.totalTreatments?.toLocaleString() || '0'}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                {stats?.trends?.treatments > 0 ? (
                  <TrendingUpIcon color="success" fontSize="small" />
                ) : (
                  <TrendingDownIcon color="error" fontSize="small" />
                )}
                <Typography 
                  variant="body2" 
                  color={stats?.trends?.treatments > 0 ? "success.main" : stats?.trends?.treatments < 0 ? "error.main" : "text.secondary"} 
                  sx={{ ml: 0.5 }}
                >
                  {formatTrendLabel(stats?.trends?.treatments)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ borderRadius: 2, mb: 3 }}>
        <CardHeader title="Pipeline & Recovery Snapshot" />
        <Divider />
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2" color="text.secondary">Open / Won / Lost</Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                <Chip size="small" label={`Open: ${stats?.pipelineAnalytics?.outcomes?.open || 0}`} />
                <Chip size="small" color="success" label={`Won: ${stats?.pipelineAnalytics?.outcomes?.won || 0}`} />
                <Chip size="small" color="error" label={`Lost: ${stats?.pipelineAnalytics?.outcomes?.lost || 0}`} />
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2" color="text.secondary">Win Rate</Typography>
              <Typography variant="h5" sx={{ mt: 1 }}>
                {stats?.pipelineAnalytics?.conversionRate || 0}%
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2" color="text.secondary">No-show Recovery</Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                <Chip size="small" label={`Open: ${stats?.pipelineAnalytics?.noShowRecovery?.open || 0}`} />
                <Chip size="small" color="primary" label={`In progress: ${stats?.pipelineAnalytics?.noShowRecovery?.in_progress || 0}`} />
                <Chip size="small" color="success" label={`Done: ${stats?.pipelineAnalytics?.noShowRecovery?.done || 0}`} />
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                <Chip size="small" variant="outlined" label={`No-show: ${stats?.pipelineAnalytics?.noShowConversion?.noShow || 0}`} />
                <Chip size="small" variant="outlined" label={`Rescheduled: ${stats?.pipelineAnalytics?.noShowConversion?.rescheduled || 0}`} />
                <Chip size="small" color="success" label={`Recovery rate: ${stats?.pipelineAnalytics?.noShowConversion?.recoveryRate || 0}%`} />
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2" color="text.secondary">Top Lost Reasons</Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                {Object.entries(stats?.pipelineAnalytics?.lostReasons || {})
                  .filter(([, count]) => Number(count) > 0)
                  .slice(0, 3)
                  .map(([reason, count]) => (
                    <Chip key={reason} size="small" variant="outlined" label={`${reason}: ${count}`} />
                  ))}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 2, mb: 3 }}>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6">Retention Control Tower (90d)</Typography>
              <Tooltip title="Rolling last 90 days retention metrics">
                <Chip size="small" variant="outlined" label="Rolling window" />
              </Tooltip>
            </Box>
          }
        />
        <Divider />
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2" color="text.secondary">Recall / Reactivation</Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                <Chip size="small" color="primary" label={`Recall: ${stats?.retentionAnalytics?.kpis?.recallReturnRate || 0}%`} />
                <Chip size="small" color="secondary" label={`Reactivation: ${stats?.retentionAnalytics?.kpis?.reactivationRate || 0}%`} />
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2" color="text.secondary">SLA / Contact</Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                <Chip size="small" color="warning" label={`SLA breaches: ${stats?.retentionAnalytics?.kpis?.slaBreachRate || 0}%`} />
                <Chip size="small" variant="outlined" label={`TTFC median: ${stats?.retentionAnalytics?.kpis?.medianTimeToFirstContactHours || 0}h`} />
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2" color="text.secondary">Campaign Conversion</Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                <Chip size="small" label={`Contacted: ${stats?.retentionAnalytics?.campaignConversion?.contactedPatients || 0}`} />
                <Chip size="small" label={`Converted: ${stats?.retentionAnalytics?.campaignConversion?.convertedPatients || 0}`} />
                <Chip size="small" color="success" label={`Rate: ${stats?.retentionAnalytics?.campaignConversion?.conversionRate || 0}%`} />
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2" color="text.secondary">Churn Risk Buckets</Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                <Chip size="small" variant="outlined" label={`Healthy: ${stats?.retentionAnalytics?.churnRiskBuckets?.healthy || 0}`} />
                <Chip size="small" variant="outlined" label={`Watchlist: ${stats?.retentionAnalytics?.churnRiskBuckets?.watchlist || 0}`} />
                <Chip size="small" color="error" label={`Churned: ${stats?.retentionAnalytics?.churnRiskBuckets?.churned || 0}`} />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Today's Appointments */}
        <Grid item xs={12} md={6} lg={4}>
          <Card sx={{ height: '100%', borderRadius: 2 }}>
            <CardHeader 
              title="Today's Appointments" 
              action={
                <Button 
                  size="small" 
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/appointments')}
                >
                  View All
                </Button>
              }
            />
            <Divider />
            <CardContent sx={{ p: 0 }}>
              <List sx={{ width: '100%', p: 0 }}>
                            {stats?.appointmentsToday?.length > 0 ? (
              stats.appointmentsToday.map((appointment, index) => (
                    <React.Fragment key={appointment.id}>
                      <ListItem
                        alignItems="flex-start"
                        secondaryAction={
                          <Chip 
                            label={appointment.status} 
                            size="small" 
                            color={appointment.status === 'confirmed' ? 'success' : 'primary'}
                            variant="outlined"
                          />
                        }
                        sx={{ px: 2 }}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'primary.light' }}>
                            <PersonIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={appointment.patientName}
                          secondary={
                            <React.Fragment>
                              <Typography
                                sx={{ display: 'inline' }}
                                component="span"
                                variant="body2"
                                color="text.primary"
                              >
                                {appointment.type}
                              </Typography>
                              {` — `}
                              <Box component="span" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                <AccessTimeIcon fontSize="small" sx={{ mr: 0.5, fontSize: 16 }} />
                                {appointment.time}
                              </Box>
                            </React.Fragment>
                          }
                        />
                      </ListItem>
                      {index < (stats?.appointmentsToday?.length || 0) - 1 && <Divider variant="inset" component="li" />}
                    </React.Fragment>
                  ))
                ) : (
                  <ListItem>
                    <ListItemText primary="No appointments scheduled for today" />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Patients */}
        <Grid item xs={12} md={6} lg={(user?.role === 'admin' || user?.role === 'manager') ? 4 : 6}>
          <Card sx={{ height: '100%', borderRadius: 2 }}>
            <CardHeader 
              title="Recent Patients" 
              action={
                <Button 
                  size="small" 
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/patients')}
                >
                  View All
                </Button>
              }
            />
            <Divider />
            <CardContent sx={{ p: 0 }}>
              <List sx={{ width: '100%', p: 0 }}>
                            {stats?.recentPatients?.length > 0 ? (
              stats.recentPatients.map((patient, index) => (
                    <React.Fragment key={patient.id}>
                      <ListItem
                        alignItems="flex-start"
                        sx={{ px: 2 }}
                        button
                        onClick={() => navigate(`/patients/${patient.id}`)}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: patient.gender === 'female' ? 'secondary.light' : 'primary.light' }}>
                            {patient.name.charAt(0)}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={patient.name}
                          secondary={
                            <React.Fragment>
                              <Typography
                                sx={{ display: 'inline' }}
                                component="span"
                                variant="body2"
                                color="text.primary"
                              >
                                {patient.phone}
                              </Typography>
                              {` — Registered on ${formatDateLocalizedSync(patient.date)}`}
                            </React.Fragment>
                          }
                        />
                      </ListItem>
                      {index < (stats?.recentPatients?.length || 0) - 1 && <Divider variant="inset" component="li" />}
                    </React.Fragment>
                  ))
                ) : (
                  <ListItem>
                    <ListItemText primary="No recent patients" />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Appointment Types Chart */}
        <Grid item xs={12} md={6} lg={4}>
          <Card sx={{ height: '100%', borderRadius: 2 }}>
            <CardHeader title="Appointment Types" />
            <Divider />
            <CardContent>
              <Box
                sx={{ height: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                role="img"
                aria-label="Appointment types distribution chart"
              >
                {(stats?.appointmentsByType?.length || 0) > 0 ? (
                  <Pie data={appointmentTypeChartData} />
                ) : (
                  <Typography variant="body2" color="text.secondary">No appointment type data available.</Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Revenue Chart - Only visible to admin and manager */}
        {(user?.role === 'admin' || user?.role === 'manager') && (
          <Grid item xs={12} lg={8}>
            <Card sx={{ borderRadius: 2 }}>
              <CardHeader title="Revenue Trend" />
              <Divider />
              <CardContent>
                <Box sx={{ height: 300 }} role="img" aria-label="Monthly revenue trend chart">
                  {(stats?.revenueByMonth?.length || 0) > 0 ? (
                    <Bar data={revenueChartData} options={revenueChartOptions} />
                  ) : (
                    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="body2" color="text.secondary">No revenue data available.</Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Upcoming Appointments */}
        <Grid item xs={12} md={6} lg={(user?.role === 'admin' || user?.role === 'manager') ? 4 : 6}>
          <Card sx={{ height: '100%', borderRadius: 2 }}>
            <CardHeader 
              title="Upcoming Appointments" 
              action={
                <Button 
                  size="small" 
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/calendar')}
                >
                  Calendar
                </Button>
              }
            />
            <Divider />
            <CardContent sx={{ p: 0 }}>
              <List sx={{ width: '100%', p: 0 }}>
                            {stats?.upcomingAppointments?.length > 0 ? (
              stats.upcomingAppointments.map((appointment, index) => (
                    <React.Fragment key={appointment.id}>
                      <ListItem
                        alignItems="flex-start"
                        sx={{ px: 2 }}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'info.light' }}>
                            <CalendarTodayIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={appointment.patientName}
                          secondary={
                            <React.Fragment>
                              <Typography
                                sx={{ display: 'inline' }}
                                component="span"
                                variant="body2"
                                color="text.primary"
                              >
                                {appointment.type}
                              </Typography>
                              {` — ${formatDateLocalizedSync(appointment.date)} at ${appointment.time}`}
                            </React.Fragment>
                          }
                        />
                      </ListItem>
                      {index < (stats?.upcomingAppointments?.length || 0) - 1 && <Divider variant="inset" component="li" />}
                    </React.Fragment>
                  ))
                ) : (
                  <ListItem>
                    <ListItemText primary="No upcoming appointments" />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;